import type { InspectionDetail, Observation } from "../domain/inspections";
import { TODAY } from "./clock";

/*
 * Sample inspections — five rounds from the airports pack's own templates
 * (airfield_daily_quick, agl_night, oema_field_round, pavement_detailed,
 * perimeter_patrol), with observations shaped as `get_inspection` returns
 * them. Failures reference the sample work orders where the story joins up:
 * the holding-position lights (WO-000005), the FOD at A2 (WO-000012), the
 * remote-apron sealant (WO-000010).
 */

function at(daysAgo: number, hour: number, minute = 0): string {
  const d = new Date(TODAY);
  d.setUTCDate(d.getUTCDate() - daysAgo);
  d.setUTCHours(hour, minute, 0, 0);
  return d.toISOString();
}

function obs(
  id: string,
  itemKey: string,
  result: Observation["result"],
  extra: Partial<Observation> = {},
): Observation {
  return { id, itemKey, result, photos: 0, ...extra };
}

const DAILY_ITEMS = [
  "fod_present",
  "pavement_surface",
  "standing_water",
  "markings_legible",
  "agl_sign_damage",
  "strip_obstacles",
  "wildlife_activity",
  "perimeter_fence",
  "worksites_marked",
  "inspector_notes",
];

function dailyRound(id: string, failures: Record<string, Partial<Observation>>, notes: string): Observation[] {
  return DAILY_ITEMS.map((key, i) => {
    if (key === "inspector_notes") return obs(`${id}-o${i}`, key, "value", { valueText: notes });
    const f = failures[key];
    return f ? obs(`${id}-o${i}`, key, "fail", f) : obs(`${id}-o${i}`, key, "pass");
  });
}

export const INSPECTIONS: InspectionDetail[] = [
  {
    id: "insp-0006",
    siteId: "site_oema",
    status: "completed",
    template: "airfield_daily_quick",
    templateName: "Daily Airfield Serviceability Inspection",
    depth: "quick",
    domain: "airfield",
    zoneCode: "RWY-17-35",
    scheduledFor: at(0, 3),
    startedAt: at(0, 3, 12),
    completedAt: at(0, 3, 58),
    score: 100,
    coveragePct: 100,
    capturedOffline: false,
    answers: 10,
    failures: 0,
    observations: dailyRound(
      "insp-0006",
      {},
      "Runway and taxiways clear. Light rubber build-up noted in TDZ 17, already under WO-000006.",
    ),
  },
  {
    id: "insp-0005",
    siteId: "site_oema",
    status: "completed",
    template: "agl_night",
    templateName: "AGL Night Serviceability Inspection",
    depth: "detailed",
    domain: "airfield",
    zoneCode: "TWY-B",
    scheduledFor: at(1, 20),
    startedAt: at(1, 20, 30),
    completedAt: at(1, 22, 5),
    score: 91.7,
    coveragePct: 100,
    capturedOffline: true,
    answers: 24,
    failures: 2,
    observations: [
      ...Array.from({ length: 22 }, (_, i) =>
        obs(`insp-0005-o${i}`, "lamp_lit", "pass", {
          assetTag: `HP-B1-${String(i + 1).padStart(2, "0")}`,
          zoneCode: "TWY-B",
        }),
      ),
      obs("insp-0005-o22", "lamp_lit", "fail", {
        assetTag: "HP-B1-03",
        zoneCode: "TWY-B",
        notes: "Unit dark at step 3 and step 5.",
        defectId: "def-agl-b1-03",
        photos: 1,
        point: { lon: 39.7014, lat: 24.5568 },
      }),
      obs("insp-0005-o23", "lamp_lit", "fail", {
        assetTag: "HP-B1-04",
        zoneCode: "TWY-B",
        notes: "Adjacent unit also dark — two consecutive.",
        defectId: "def-agl-b1-04",
        photos: 1,
        point: { lon: 39.7015, lat: 24.5567 },
      }),
    ],
  },
  {
    id: "insp-0004",
    siteId: "site_oema",
    status: "completed",
    template: "oema_field_round",
    templateName: "OEMA Field Round",
    depth: "detailed",
    domain: "airfield",
    zoneCode: "APRON-R",
    scheduledFor: at(3, 5),
    startedAt: at(3, 5, 20),
    completedAt: at(3, 7, 10),
    score: 83.3,
    coveragePct: 92,
    capturedOffline: true,
    answers: 12,
    failures: 2,
    observations: [
      obs("insp-0004-o0", "pavement", "fail", {
        zoneCode: "APRON-R",
        valueText: "find",
        notes: "Joint sealant failed across slabs 14–16; loose material at the joints.",
        defectId: "def-apron-r-sealant",
        photos: 3,
        point: { lon: 39.7196, lat: 24.5457 },
      }),
      obs("insp-0004-o1", "markings", "pass", { zoneCode: "APRON-R", valueText: "pass" }),
      obs("insp-0004-o2", "drainage", "pass", { zoneCode: "APRON-R", valueText: "pass" }),
      obs("insp-0004-o3", "fod", "fail", {
        zoneCode: "TWY-A",
        valueText: "find",
        notes: "Loose aggregate at holding position A2 after adjacent works.",
        defectId: "def-fod-a2",
        photos: 2,
        point: { lon: 39.7058, lat: 24.5562 },
      }),
      obs("insp-0004-o4", "lighting", "pass", { zoneCode: "APRON-R", valueText: "pass" }),
      obs("insp-0004-o5", "signs", "pass", { zoneCode: "TWY-A", valueText: "pass" }),
      obs("insp-0004-o6", "fencing", "pass", { valueText: "pass" }),
      obs("insp-0004-o7", "wildlife", "pass", { valueText: "pass" }),
      obs("insp-0004-o8", "vehicles", "pass", { valueText: "pass" }),
      obs("insp-0004-o9", "works", "pass", { valueText: "pass" }),
      obs("insp-0004-o10", "weather_effects", "na", { skippedReason: "Dry, no wind — not applicable." }),
      obs("insp-0004-o11", "notes", "value", {
        valueText: "Sealant to be ordered; FOD swept and Ops response raised immediately.",
      }),
    ],
  },
  {
    id: "insp-0003",
    siteId: "site_oema",
    status: "completed",
    template: "pavement_detailed",
    templateName: "Detailed Pavement Condition Inspection",
    depth: "detailed",
    domain: "airfield",
    zoneCode: "RWY-17-35",
    scheduledFor: at(8, 22),
    startedAt: at(8, 22, 15),
    completedAt: at(8, 23, 50),
    score: 94.4,
    coveragePct: 100,
    capturedOffline: false,
    answers: 18,
    failures: 1,
    observations: [
      ...Array.from({ length: 17 }, (_, i) =>
        obs(`insp-0003-o${i}`, "pci_segment", "value", {
          zoneCode: "RWY-17-35",
          valueNum: 78 + ((i * 7) % 15),
          valueText: `CH ${i * 250}–${(i + 1) * 250}`,
        }),
      ),
      obs("insp-0003-o17", "rubber_contamination", "fail", {
        zoneCode: "RWY-17-35",
        valueText: "TDZ 17, CH 300–900",
        notes: "Rubber build-up beyond the friction limit; ties to survey FR-2026-07-11.",
        defectId: "def-rubber-tdz17",
        photos: 2,
        point: { lon: 39.6969, lat: 24.5628 },
      }),
    ],
  },
  {
    id: "insp-0002",
    siteId: "site_oema",
    status: "in_progress",
    template: "perimeter_patrol",
    templateName: "Perimeter Patrol",
    depth: "quick",
    domain: "landside",
    scheduledFor: at(0, 4),
    startedAt: at(0, 4, 5),
    coveragePct: 45,
    capturedOffline: true,
    answers: 5,
    failures: 0,
    observations: Array.from({ length: 5 }, (_, i) =>
      obs(`insp-0002-o${i}`, "fence_section_intact", "pass", { valueText: `Section ${i + 1}` }),
    ),
  },
  {
    id: "insp-0001",
    siteId: "site_oema",
    status: "scheduled",
    template: "airfield_daily_quick",
    templateName: "Daily Airfield Serviceability Inspection",
    depth: "quick",
    domain: "airfield",
    zoneCode: "RWY-17-35",
    scheduledFor: at(-1, 3),
    answers: 0,
    failures: 0,
    observations: [],
  },
];
